import { createSelector } from 'reselect';

import type { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';
import {
  UI_THEME_GOLD,
  UI_THEME_PLATINUM,
  UI_THEME_REGULAR,
  UI_THEME_UNKNOWN,
  UserTheme,
} from 'Lib/UiThemeContext';

import {
  getHasGoldSubscription,
  getHasPlatinumSubscription,
} from 'Store/profile/revenue.selectors';

/**
 * Gets app namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {AppState} entities by type
 */
export const selectApp = createKeySelector('app');

/**
 * Gets whether a lifecycle hook has been triggered
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true when the lifecycle hook has already fired
 */
export const getIsLifecycleTriggered = createSelector(
  [selectApp, (state: GlobalStateType, lifecycle: string) => lifecycle],
  (app, lifecycle) => app.lifecycles?.[lifecycle] ?? false
);

/**
 * Gets whether this is the active tab from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true when this is the active tab
 */
export const getIsActiveTab = createSelector(selectApp, (app) => app.activeTab);

/**
 * Gets whether app is offline from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true when app is offline
 */
export const getIsOffline = createSelector(selectApp, (app) => app.offline);

/**
 * Gets the 3DS transaction status from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} language
 */
export const getIs3DSComplete = createSelector(selectApp, (app) => app.is3dsComplete);

/**
 * Gets the loading state of the profile call
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} loading state
 */
export const getIsProfileLoaded = createSelector(selectApp, (app) => app.profileLoaded);

/**
 * Gets the loading state of the pre-auth levers
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} loading state
 */
export const getIsPreAuthLeversLoaded = createSelector(selectApp, (app) => app.preAuthLeversLoaded);

export const getDobViolationDaysLeft = createSelector(selectApp, (app) => app.dobViolationDaysLeft);

export const getIsDeviceFlaggedForDobViolation = createSelector(
  selectApp,
  (app) => app.deviceFlaggedForDobViolation
);

export const getUiTheme = createSelector(
  [getHasGoldSubscription, getHasPlatinumSubscription, getIsProfileLoaded],
  (hasGoldSubscription, hasPlatinumSubscription, isProfileLoaded): UserTheme => {
    if (!isProfileLoaded) {
      return UI_THEME_UNKNOWN;
    }
    if (hasPlatinumSubscription) {
      return UI_THEME_PLATINUM;
    }
    if (hasGoldSubscription) {
      return UI_THEME_GOLD;
    }
    return UI_THEME_REGULAR;
  }
);

export const getAvPermissionStatus = createSelector(selectApp, (app) => app.avPermissionStatus);

export const getIsPurchaseReducerRehydrated = createSelector(selectApp, (app) =>
  Boolean(app.lifecycles?.rehydratePurchase)
);

export const getIsMMReducerRehydrated = createSelector(selectApp, (app) =>
  Boolean(app.lifecycles?.rehydrateMm)
);

export const getIsVaultReducerRehydrated = createSelector(selectApp, (app) =>
  Boolean(app.lifecycles?.rehydrateVault)
);

export const getIsTinderSelectReducerRehydrated = createSelector(selectApp, (app) =>
  Boolean(app.lifecycles?.rehydrateTinderSelect)
);

export const getBranding = createSelector(selectApp, (app) => app.branding);

export const getAppSource = createSelector(selectApp, (app) => app.appSource);

export const getAuthAttributionData = createSelector(selectApp, (app) => app.authAttributionData);

export const getDeeplinkSource = createSelector(selectApp, (app) => app.deeplinkSource);
